import React, { useEffect, useState, useRef } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import FormField, { Input, Select } from '../../components/FormField';

export default function AdminSponsors() {
  const [events, setEvents]           = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [sponsors, setSponsors]       = useState([]);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState({ name: '', website: '', sort_order: 0 });
  const [logoFile, setLogoFile]       = useState(null);
  const [preview, setPreview]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const fileRef                       = useRef();

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); }, []);

  const load = () => {
    if (!selectedEvent) return;
    api.get(`/api/events/${selectedEvent}/sponsors`).then((r) => setSponsors(r.data));
  };
  useEffect(() => { load(); }, [selectedEvent]); // eslint-disable-line

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const openNew = () => {
    setForm({ name: '', website: '', sort_order: sponsors.length });
    setLogoFile(null); setPreview(null); setEditing('new'); setError('');
  };

  const openEdit = (s) => {
    setForm({ name: s.name, website: s.website || '', sort_order: s.sort_order });
    setLogoFile(null);
    setPreview(s.logo_path ? `${s.logo_path}?t=${Date.now()}` : null);
    setEditing(s.id); setError('');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('event_id', selectedEvent);
      fd.append('name', form.name);
      fd.append('website', form.website);
      fd.append('sort_order', form.sort_order);
      if (logoFile) fd.append('logo', logoFile);

      if (editing === 'new') {
        await api.post('/api/admin/sponsors', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        await api.put(`/api/admin/sponsors/${editing}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setEditing(null); load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setLoading(false); }
  };

  const deleteSponsor = async (id) => {
    if (!window.confirm('Sponsor wirklich löschen?')) return;
    await api.delete(`/api/admin/sponsors/${id}`);
    load();
  };

  const deleteLogo = async (id) => {
    await api.delete(`/api/admin/sponsors/${id}/logo`);
    setPreview(null); load();
  };

  return (
    <div>
      <h2 className="page-title">Sponsoren</h2>

      <FormField label="Event auswählen" className="max-w-sm mb-6">
        <Select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">– Event wählen –</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
        </Select>
      </FormField>

      {selectedEvent && (
        <>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">{sponsors.length} Sponsor(en)</p>
            <Button onClick={openNew} size="sm">+ Sponsor hinzufügen</Button>
          </div>

          {/* Formular */}
          {editing && (
            <div className="card border border-ocean-200 mb-6">
              <h3 className="section-title">{editing === 'new' ? 'Neuer Sponsor' : 'Sponsor bearbeiten'}</h3>
              {error && <div className="alert-error mb-4">{error}</div>}
              <form onSubmit={save}>
                <div className="grid sm:grid-cols-2 gap-x-4">
                  <FormField label="Name" required>
                    <Input value={form.name} onChange={set('name')} placeholder="z.B. ACME GmbH" required />
                  </FormField>
                  <FormField label="Website (optional)">
                    <Input value={form.website} onChange={set('website')} placeholder="https://example.com" />
                  </FormField>
                  <FormField label="Reihenfolge">
                    <Input type="number" value={form.sort_order} onChange={set('sort_order')} className="w-24" />
                  </FormField>
                </div>

                {/* Logo Upload */}
                <FormField label="Logo (PNG, JPG, SVG — max. 2 MB)">
                  <div className="flex items-start gap-4">
                    {preview && (
                      <div className="relative flex-shrink-0">
                        <img src={preview} alt="Logo Vorschau"
                          className="h-20 w-auto max-w-48 object-contain border border-slate-200 rounded p-1 bg-white" />
                        {editing !== 'new' && (
                          <button type="button" onClick={() => deleteLogo(editing)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center leading-none cursor-pointer border-0">
                            ×
                          </button>
                        )}
                      </div>
                    )}
                    <div>
                      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
                        {preview ? 'Logo ändern' : 'Logo hochladen'}
                      </Button>
                      {logoFile && <p className="text-xs text-slate-400 mt-1">{logoFile.name}</p>}
                    </div>
                  </div>
                </FormField>

                <div className="flex gap-2">
                  <Button type="submit" loading={loading}>Speichern</Button>
                  <Button variant="ghost" type="button" onClick={() => setEditing(null)}>Abbrechen</Button>
                </div>
              </form>
            </div>
          )}

          {/* Sponsor-Liste */}
          {sponsors.length === 0 && !editing ? (
            <div className="card text-center py-8 text-slate-400 text-sm">Noch keine Sponsoren für dieses Event.</div>
          ) : (
            <div className="space-y-3">
              {sponsors.map((s) => (
                <div key={s.id} className="card flex items-center gap-4">
                  <div className="w-24 h-14 flex-shrink-0 flex items-center justify-center bg-slate-50 border border-slate-200 rounded">
                    {s.logo_path ? (
                      <img src={s.logo_path} alt={s.name} className="max-h-12 max-w-20 object-contain" />
                    ) : (
                      <span className="text-xs text-slate-400">Kein Logo</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-ocean-900">{s.name}</p>
                    {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-xs text-ocean-500 hover:underline">{s.website}</a>}
                    <p className="text-xs text-slate-400">Reihenfolge: {s.sort_order}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="secondary" size="sm" onClick={() => openEdit(s)}>Bearbeiten</Button>
                    <Button variant="danger" size="sm" onClick={() => deleteSponsor(s.id)}>Löschen</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Vorschau */}
          {sponsors.length > 0 && (
            <div className="mt-8">
              <p className="section-title">Vorschau Slideshow</p>
              <SponsorSlideshow sponsors={sponsors} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SponsorSlideshow({ sponsors }) {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (sponsors.length <= 1) return;
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setIdx((i) => (i + 1) % sponsors.length); setFade(true); }, 400);
    }, 3500);
    return () => clearInterval(t);
  }, [sponsors.length]);

  const s = sponsors[idx];
  return (
    <div className="bg-ocean-900 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-ocean-800">
        <p className="text-xs font-bold text-ocean-400 uppercase tracking-widest text-center">Unsere Sponsoren</p>
      </div>
      <div className="flex flex-col items-center justify-center py-8 px-6 min-h-36"
        style={{ opacity: fade ? 1 : 0, transition: 'opacity 0.4s ease' }}>
        {s.logo_path ? (
          <>
            <div style={{ background: '#fff', borderRadius: 8, padding: '12px 24px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 160, minHeight: 80 }}>
              <img src={s.logo_path} alt={s.name} style={{ maxHeight: 72, maxWidth: 240, objectFit: 'contain', display: 'block' }} />
            </div>
            <p className="text-ocean-200 text-sm font-medium">{s.name}</p>
          </>
        ) : (
          <p className="text-3xl font-extrabold text-white tracking-tight mb-3">{s.name}</p>
        )}
        {s.website && (
          <a href={s.website} target="_blank" rel="noreferrer"
            className="text-ocean-400 text-xs mt-1 hover:text-ocean-200 no-underline hover:underline">
            {s.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>
      {sponsors.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-4">
          {sponsors.map((_, i) => (
            <button key={i} onClick={() => { setFade(false); setTimeout(() => { setIdx(i); setFade(true); }, 200); }}
              className={`w-1.5 h-1.5 rounded-full border-0 cursor-pointer transition-all ${i === idx ? 'bg-sand-400 w-4' : 'bg-ocean-600'}`} />
          ))}
        </div>
      )}
    </div>
  );
}
